/**
 * CreateInvoiceDialog - 청구서 생성 다이얼로그
 * 
 * 기능:
 * - 품목 동적 추가/삭제
 * - 세금 자동 계산 (10%)
 * - 미리보기
 * - 청구서 생성
 */

import { Trans, t } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { CalendarDays, Plus, Trash2, X, FileText } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Textarea } from '../../../components/ui/textarea';
import { BillingDocument, BillingDocumentItem, DEFAULT_ISSUER_JP, generateDocumentId } from '../../../models/BillingDocument';
import { BillingInfo } from '../../../models/WorkspaceAdmin';
import { Fire } from '../../../services/FireClient';
import { doc, getDoc, collection, addDoc, serverTimestamp } from 'firebase/firestore';

interface Props {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    workspaceId: string;
    onCreated: () => void;
}

interface InvoiceItem {
    description: string;
    quantity: number;
    unitPrice: number;
}

const TAX_RATE = 0.1; // 10% 소비세

const CreateInvoiceDialog: React.FC<Props> = ({ open, onOpenChange, workspaceId, onCreated }) => {
    const { i18n } = useLingui();
    const [loading, setLoading] = useState(false);
    const [billingInfo, setBillingInfo] = useState<Partial<BillingInfo> | null>(null);

    // 폼 상태
    const [items, setItems] = useState<InvoiceItem[]>([
        { description: '', quantity: 1, unitPrice: 0 }
    ]);
    const [dueDate, setDueDate] = useState<string>('');
    const [notes, setNotes] = useState('');
    const [status, setStatus] = useState<'draft' | 'issued'>('issued');

    // 워크스페이스 청구 정보 로드
    useEffect(() => {
        if (open && workspaceId) {
            loadBillingInfo();
        }
    }, [open, workspaceId]);

    const loadBillingInfo = async () => {
        try {
            const wsDoc = await getDoc(doc(Fire.store, 'workspaces', workspaceId));
            if (wsDoc.exists()) {
                const data = wsDoc.data();
                setBillingInfo(data.billingInfo || data.companyInfo || null);
            }
        } catch (e) {
            console.error('Failed to load billing info:', e);
        }
    };

    // 금액 계산
    const calculateSubtotal = () => {
        return items.reduce((sum, item) => sum + (item.quantity * item.unitPrice), 0);
    };

    const calculateTax = () => {
        return Math.floor(calculateSubtotal() * TAX_RATE);
    };

    const calculateTotal = () => {
        return calculateSubtotal() + calculateTax();
    };

    // 품목 관리
    const addItem = () => {
        setItems([...items, { description: '', quantity: 1, unitPrice: 0 }]);
    };

    const removeItem = (index: number) => {
        if (items.length > 1) {
            setItems(items.filter((_, i) => i !== index));
        }
    };

    const updateItem = (index: number, field: keyof InvoiceItem, value: string | number) => {
        const newItems = [...items];
        if (field === 'description') {
            newItems[index].description = value as string;
        } else if (field === 'quantity') {
            newItems[index].quantity = Math.max(1, parseInt(value as string) || 1);
        } else if (field === 'unitPrice') {
            newItems[index].unitPrice = Math.max(0, parseInt(value as string) || 0);
        }
        setItems(newItems);
    };

    // 청구서 생성
    const handleCreate = async () => {
        // 유효성 검사
        const validItems = items.filter(item => item.description.trim() && item.unitPrice > 0);
        if (validItems.length === 0) {
            alert(t(i18n)`품목을 하나 이상 입력해주세요.`);
            return;
        }

        setLoading(true);
        try {
            const now = new Date();
            const invoiceNumber = generateDocumentId('INVOICE', now);

            const invoiceData: Omit<BillingDocument, 'id'> = {
                type: 'INVOICE',
                country: 'JP',
                workspaceId,

                // 금액
                subtotal: calculateSubtotal(),
                taxRate: TAX_RATE,
                taxAmount: calculateTax(),
                total: calculateTotal(),
                currency: 'JPY',

                // 당사자
                issuer: DEFAULT_ISSUER_JP,
                recipient: billingInfo || {},

                // 품목
                items: validItems.map(item => ({
                    description: item.description,
                    quantity: item.quantity,
                    unitPrice: item.unitPrice,
                    amount: item.quantity * item.unitPrice,
                })),

                // 날짜
                issueDate: now,
                dueDate: dueDate ? new Date(dueDate) : undefined,

                // 메타
                status,
                notes: notes || undefined,
                createdBy: 'super-admin', // TODO: 실제 관리자 ID
                createdAt: now,
            };

            // Firestore에 저장
            const colRef = collection(Fire.store, 'workspaces', workspaceId, 'billing_documents');
            await addDoc(colRef, {
                ...invoiceData,
                invoiceNumber,
                issueDate: serverTimestamp(),
                createdAt: serverTimestamp(),
            });

            // 성공 후 초기화
            setItems([{ description: '', quantity: 1, unitPrice: 0 }]);
            setDueDate('');
            setNotes('');
            onCreated();
            onOpenChange(false);

        } catch (e) {
            console.error('Failed to create invoice:', e);
            alert(t(i18n)`청구서 생성에 실패했습니다.`);
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('ja-JP', { style: 'currency', currency: 'JPY' }).format(amount);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <FileText className="w-5 h-5" />
                        <Trans>請求書作成</Trans>
                    </DialogTitle>
                    <DialogDescription>
                        <Trans>新しい請求書を作成します。</Trans>
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-6 py-4">
                    {/* 수신자 정보 */}
                    <div className="bg-slate-50 rounded-lg p-4">
                        <h3 className="text-sm font-medium text-slate-700 mb-2">
                            <Trans>請求先</Trans>
                        </h3>
                        {billingInfo?.companyName ? (
                            <div className="text-sm text-slate-600">
                                <div className="font-medium">{billingInfo.companyName}</div>
                                {billingInfo.representative && <div>{billingInfo.representative} 様</div>}
                                {billingInfo.address && <div>{billingInfo.address}</div>}
                            </div>
                        ) : (
                            <p className="text-sm text-slate-500">
                                <Trans>請求先情報が登録されていません。</Trans>
                            </p>
                        )}
                    </div>

                    {/* 품목 */}
                    <div>
                        <div className="flex items-center justify-between mb-3">
                            <Label className="text-sm font-medium">
                                <Trans>明細</Trans>
                            </Label>
                            <Button type="button" variant="outline" size="sm" onClick={addItem}>
                                <Plus className="w-4 h-4 mr-1" />
                                <Trans>追加</Trans>
                            </Button>
                        </div>
                        <div className="space-y-3">
                            {items.map((item, index) => (
                                <div key={index} className="flex gap-2 items-start">
                                    <div className="flex-1">
                                        <Input
                                            placeholder={t(i18n)`品目名`}
                                            value={item.description}
                                            onChange={(e) => updateItem(index, 'description', e.target.value)}
                                        />
                                    </div>
                                    <div className="w-20">
                                        <Input
                                            type="number"
                                            placeholder={t(i18n)`数量`}
                                            value={item.quantity}
                                            onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                                            min={1}
                                        />
                                    </div>
                                    <div className="w-32">
                                        <Input
                                            type="number"
                                            placeholder={t(i18n)`単価`}
                                            value={item.unitPrice || ''}
                                            onChange={(e) => updateItem(index, 'unitPrice', e.target.value)}
                                            min={0}
                                        />
                                    </div>
                                    <div className="w-28 text-right text-sm pt-2 text-slate-600">
                                        {formatCurrency(item.quantity * item.unitPrice)}
                                    </div>
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => removeItem(index)}
                                        disabled={items.length === 1}
                                        className="text-slate-400 hover:text-red-500"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </Button>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* 합계 */}
                    <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                        <div className="flex justify-between text-sm">
                            <span className="text-slate-600"><Trans>小計</Trans></span>
                            <span>{formatCurrency(calculateSubtotal())}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                            <span className="text-slate-600"><Trans>消費税 (10%)</Trans></span>
                            <span>{formatCurrency(calculateTax())}</span>
                        </div>
                        <div className="flex justify-between text-lg font-bold border-t pt-2">
                            <span><Trans>合計</Trans></span>
                            <span className="text-indigo-600">{formatCurrency(calculateTotal())}</span>
                        </div>
                    </div>

                    {/* 지불 기한 */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <Label className="text-sm mb-1.5 block">
                                <Trans>支払期限</Trans>
                            </Label>
                            <Input
                                type="date"
                                value={dueDate}
                                onChange={(e) => setDueDate(e.target.value)}
                            />
                        </div>
                        <div>
                            <Label className="text-sm mb-1.5 block">
                                <Trans>ステータス</Trans>
                            </Label>
                            <Select value={status} onValueChange={(v) => setStatus(v as 'draft' | 'issued')}>
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="draft"><Trans>下書き</Trans></SelectItem>
                                    <SelectItem value="issued"><Trans>発行済み</Trans></SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    {/* 비고 */}
                    <div>
                        <Label className="text-sm mb-1.5 block">
                            <Trans>備考</Trans>
                        </Label>
                        <Textarea
                            placeholder={t(i18n)`備考欄に記載する内容`}
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            rows={3}
                        />
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>
                        <Trans>キャンセル</Trans>
                    </Button>
                    <Button
                        onClick={handleCreate}
                        disabled={loading || calculateTotal() === 0}
                        className="bg-indigo-600 hover:bg-indigo-700"
                    >
                        {loading ? (
                            <Trans>作成中...</Trans>
                        ) : (
                            <Trans>請求書を作成</Trans>
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default CreateInvoiceDialog;
